import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Select from '@/components/ui/Select';
import type { StatementLayout } from '@/types';
import { sectionLabel, formatPeriod } from '@/utils/format';

function generateUpcomingPeriods(count = 24): string[] {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  const fyEnd = month >= 8 ? year + 1 : year;
  const pCurrent = month >= 8 ? month - 7 : month + 5;
  const periods: string[] = [];
  let fy = fyEnd;
  let p = pCurrent;
  for (let i = 0; i < count; i++) {
    periods.push(`${String(fy).slice(2)}-P${p}`);
    p++;
    if (p > 12) { p = 1; fy++; }
  }
  return periods;
}

async function listLayouts(): Promise<StatementLayout[]> {
  const { data } = await apiClient.get('/statement-layout/versions');
  return data.data ?? data;
}

async function createLayout(payload: { effectiveFrom: string; items: unknown[] }): Promise<StatementLayout> {
  const { data } = await apiClient.post('/statement-layout', payload);
  return data.data ?? data;
}

export default function StatementLayoutAdminPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const upcomingPeriods = generateUpcomingPeriods(24);
  const [effectiveFrom, setEffectiveFrom] = useState(upcomingPeriods[0]);
  const [createError, setCreateError] = useState('');

  const { data: layouts, isLoading } = useQuery({
    queryKey: ['statement-layouts'],
    queryFn: listLayouts,
  });

  const activeLayout = layouts?.find((l) => l.isActive);
  const allLayouts = layouts ? [...layouts].sort((a, b) => b.version - a.version) : [];

  const createMutation = useMutation({
    mutationFn: createLayout,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['statement-layouts'] });
      setShowCreate(false);
      setEffectiveFrom('');
    },
    onError: (err: unknown) => {
      setCreateError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Create failed',
      );
    },
  });

  const handleCreate = () => {
    if (!activeLayout) {
      setCreateError('No active layout to base new version on.');
      return;
    }
    createMutation.mutate({
      effectiveFrom,
      items: activeLayout.items as unknown[],
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Statement Layout</h1>
          <p className="mt-1 text-sm text-gray-500">
            Versioned layout mapping CF codes to statement sections. A new version
            takes effect from the specified period.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          New version
        </Button>
      </div>

      {/* Active layout banner */}
      {activeLayout && (
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Active layout: v{activeLayout.version}
              </p>
              <p className="text-xs text-gray-500">
                Effective from {formatPeriod(activeLayout.effectiveFrom)} (
                {activeLayout.effectiveFrom})
              </p>
            </div>
            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
              Active
            </span>
          </div>
        </Card>
      )}

      {/* Layout preview */}
      {activeLayout && (
        <div>
          <h2 className="mb-3 text-base font-semibold text-gray-900">
            Layout preview — v{activeLayout.version}
          </h2>
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            {(() => {
              const items = activeLayout.items as Array<{
                type: string;
                cfCode?: string;
                label?: string;
                section: string;
                sortOrder: number;
              }>;
              const bySection = items.reduce<Record<string, typeof items>>(
                (acc, item) => {
                  const s = item.section;
                  if (!acc[s]) acc[s] = [];
                  acc[s].push(item);
                  return acc;
                },
                {},
              );
              return Object.entries(bySection).map(([section, sectionItems]) => (
                <div key={section} className="border-b border-gray-100 last:border-b-0">
                  <div className="bg-gray-50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-600">
                    {sectionLabel(section)}
                  </div>
                  {sectionItems.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-4 px-4 py-1.5 text-sm border-b border-gray-50 last:border-b-0"
                    >
                      <span className="w-16 font-mono text-xs text-gray-600">
                        {item.cfCode ?? item.type}
                      </span>
                      <span className="text-gray-700">{item.label ?? '—'}</span>
                      <span className="ml-auto text-xs text-gray-600">{item.type}</span>
                    </div>
                  ))}
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* Version history */}
      {allLayouts.length > 0 && (
        <div>
          <h2 className="mb-3 text-base font-semibold text-gray-900">Version history</h2>
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Version</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Effective from</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Items</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {allLayouts.map((layout) => (
                  <tr key={layout.id}>
                    <td className="px-4 py-2 font-medium text-gray-900">v{layout.version}</td>
                    <td className="px-4 py-2 text-gray-600">
                      {formatPeriod(layout.effectiveFrom)} ({layout.effectiveFrom})
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {(layout.items as unknown[]).length} items
                    </td>
                    <td className="px-4 py-2">
                      {layout.isActive ? (
                        <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Active</span>
                      ) : (
                        <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">Superseded</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-600 text-xs">
                      {new Date(layout.createdAt).toLocaleDateString('en-GB')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">New layout version</h2>
            <p className="mb-4 text-sm text-gray-500">
              Creates a copy of the current active layout with a new effective-from period.
              Historic periods continue to use the previous version.
            </p>
            {createError && (
              <div role="alert" className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                {createError}
              </div>
            )}
            <Select
              label="Effective from"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              options={upcomingPeriods.map((p) => ({ value: p, label: formatPeriod(p) }))}
            />
            <div className="mt-4 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button loading={createMutation.isPending} onClick={handleCreate}>
                Create
              </Button>
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <p className="text-sm text-gray-500">Loading layouts…</p>
      )}
    </div>
  );
}
